import { Box, HStack } from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";
import moment from "moment";
import { useMemo, useState } from "react";
import { api } from "src/api";
import { Query } from "src/api/generated/types";
import { useClientById, useMe } from "src/hooks";
import { useTheme } from "src/hooks/useTheme";
import { Charts } from "./Charts";
import { FullServiceContext, getStartingYearBracket } from "./context";
import Help from "./Help";
import FullServiceMarketing from "./Marketing";
import { Overview } from "./Overview";
import { Quarters } from "./Quarters";
import Review from "./Review";

export const FullService = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { me } = useMe();
  const { client } = useClientById(clientId || "");

  const startingYearBracket = getStartingYearBracket();
  const [year, setYear] = useState(startingYearBracket);
  const canSee = me?.isSuperuser || client?.hasFullService;

  const variables = useMemo(
    () => ({
      year: year.taxYear,
      clientId,
    }),
    [year, clientId]
  );

  const { data } = useQuery<Pick<Query, "getMonthlyPoints">>(
    api.reports.monthlyPoints,
    {
      variables,
      fetchPolicy: "cache-and-network",
    }
  );

  const points = data?.getMonthlyPoints ?? [];

  const handleYearChange = (newYear: string) => {
    setYear({
      end: moment().year(parseInt(newYear)).endOf("year").toDate(),
      endFormatted: moment()
        .year(parseInt(newYear))
        .endOf("year")
        .format("YYYY-MM-DD"),
      start: moment().year(parseInt(newYear)).startOf("year").toDate(),
      startFormatted: moment()
        .year(parseInt(newYear))
        .startOf("year")
        .format("YYYY-MM-DD"),
      taxYear: newYear,
    });
  };

  if (!canSee) {
    return <FullServiceMarketing />;
  }

  return (
    <FullServiceContext.Provider
      value={{
        defaultTaxYear: startingYearBracket.taxYear,
        year,
        client,
        monthlyPoints: points,
        setYear: handleYearChange,
      }}
    >
      <HStack
        style={{
          background: theme.background,
          width: "100%",
          padding: "0 30px",
          height: "100vh",
          overflowY: "hidden",
        }}
      >
        <Box
          style={{
            flex: 1,
            paddingTop: 20,
            overflowY: "scroll",
            paddingRight: "20px",
            height: "100%",
          }}
        >
          <Charts />
          <Quarters />
          <Review />
        </Box>
        <Box
          style={{
            width: 350,
            paddingLeft: 0,
            height: "100%",
            marginLeft: 0,
            paddingTop: 20,
            alignSelf: "flex-start",
          }}
        >
          <Help />
          <Overview />
        </Box>
      </HStack>
    </FullServiceContext.Provider>
  );
};

export default FullService;
